import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '@aquabox/shared';
import '../styles/columns.css';

const STORAGE_PREFIX = 'aquabox.cols.';
const MIN_WIDTH = 48;
const MAX_WIDTH = 800;
const DRAGGING_CLASS = 'col-resizing';

interface ColumnWidthsResult {
  style: React.CSSProperties | undefined;
  headerRef: React.RefObject<HTMLDivElement | null>;
  resizer: (index: number) => React.ReactNode;
  reset: () => void;
}

/**
 * localStorage'dan kayıtlı sütun genişliklerini okur. Dizi `count` uzunluğunda DEĞİLSE ya
 * da bir elemanı geçerli pozitif px değilse `[]` döner — bu durumda "özel genişlik yok"
 * anlamına gelir ve tablo CSS varsayılanıyla (içerik + son sütunda minmax) boyutlanır.
 *
 * İki yerde çağrılır: ilk yükleme ve `storageKey` değişimi (tablo adı değişince eski
 * tablonun kaydı okunmasın diye). Tek noktada toplamak, doğrulama mantığının iki yerde
 * ayrışmasını engeller.
 */
function readStored(key: string, count: number): number[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown[];
    if (!Array.isArray(parsed) || parsed.length !== count) return [];
    const nums = parsed.map((v) => (typeof v === 'number' && v > 0 && v <= MAX_WIDTH ? v : NaN));
    if (nums.some(isNaN)) return [];
    return nums;
  } catch {
    return [];
  }
}

/**
 * Sütun genişliklerini tutamaçla sürükleyerek ayarlamaya olanak tanır.
 * Hook her çağrıldığında `storageKey`'i okur — anahtar değişince yeni kayıt otomatik yüklenir.
 *
 * State tipi `number[]`'dir (elemanlar ya hep number, ya dizi boş). Sürükleme sırasında
 * diğer sütunların genişlikleri `draggingRef.initialWidths`'ten gelir; bu sayede ilk
 * sürüklemede bile tablonun geri kalanı 80px'e düşmez.
 */
export function useColumnWidths(tableKey: string, columnCount: number): ColumnWidthsResult {
  const storageKey = STORAGE_PREFIX + tableKey;
  const headerRef = useRef<HTMLDivElement | null>(null);
  const draggingRef = useRef<{ index: number; initialWidths: number[]; startX: number } | null>(
    null,
  );
  // Her zaman güncel widths'i okumak için ref — state kapanışta eskimiş olabilir.
  const widthsRef = useRef<number[]>([]);
  // Kayıtlı genişlikler: her eleman px cinsinden number. Boş = özelleştirme yok.
  const [widths, setWidths] = useState<number[]>(() => readStored(storageKey, columnCount));

  // widths değişince ref'i de güncelle — böylece onPointerMove/onPointerUp her zaman güncel değeri okur.
  useEffect(() => {
    widthsRef.current = widths;
  }, [widths]);

  // Kayıtlı genişlikler değiştiğinde otomatik yeniden yükle (anahtar değişince).
  useEffect(() => {
    setWidths(readStored(storageKey, columnCount));
  }, [storageKey, columnCount]);

  // Mobil ortamda tutamaçlar görünmez.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 639px)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(max-width: 639px)');
    if (!mq) return;
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  /**
   * localStorage'a yazmanın TEK yolu — state'i, ref'i ve diski birlikte günceller.
   * reset ve onPointerUp buradan geçer; persist'i atlayan hiçbir yol yok.
   * Boş dizi depoya yazılmaz, anahtar silinir (eski davranışla uyumlu).
   */
  const persist = useCallback(
    (next: number[]) => {
      setWidths(next);
      widthsRef.current = next;
      try {
        if (next.length === 0) {
          localStorage.removeItem(storageKey);
        } else {
          localStorage.setItem(storageKey, JSON.stringify(next));
        }
      } catch {
        // localStorage dolu veya kullanılamıyor — bellekte devam eder.
      }
    },
    [storageKey],
  );

  const buildTemplate = useCallback((w: number[]) => {
    const parts = w.map((px, i) => (i === w.length - 1 ? `minmax(${px}px, 1fr)` : `${px}px`));
    return parts.join(' ');
  }, []);

  // Boş widths = özelleştirme yok; tablo CSS varsayılanıyla boyutlanır.
  const style: React.CSSProperties | undefined =
    isMobile || widths.length === 0 ? undefined : { gridTemplateColumns: buildTemplate(widths) };

  const reset = useCallback(() => {
    persist([]);
  }, [persist]);

  // ── Sürükleme mantığı ──────────────────────────────────────────────────────

  const onPointerMove = useCallback((e: PointerEvent) => {
    const d = draggingRef.current;
    if (!d) return;
    const delta = e.clientX - d.startX;
    const prev = d.initialWidths[d.index] ?? MIN_WIDTH;
    const next = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, prev + delta));
    // `d.initialWidths` TÜM sütunların başlangıç genişliklerini tutar; yalnız değişen
    // sütunu günceller, gerisini korur. İlk sürükleme bug'ının çözümü burasıydı:
    // `[...prevWidths]` ile başlamak, state boşken diğer sütunları `undefined` bırakıyor
    // ve `buildTemplate` onları 80px'e düşürüyordu.
    const updated = d.initialWidths.map((w, i) => (i === d.index ? next : w));
    setWidths(updated);
    widthsRef.current = updated;
  }, []);

  const onPointerUp = useCallback(() => {
    document.body.classList.remove(DRAGGING_CLASS);
    document.removeEventListener('pointermove', onPointerMove);
    document.removeEventListener('pointerup', onPointerUp);
    const d = draggingRef.current;
    draggingRef.current = null;
    if (!d) return;
    // State'i ref üzerinden diske yaz — `persist` her şeyi tek noktada toplar.
    persist(widthsRef.current);
  }, [onPointerMove, persist]);

  // Bileşen unmount olduğunda dinleyicileri temizle.
  useEffect(() => {
    return () => {
      document.body.classList.remove(DRAGGING_CLASS);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
    };
  }, [onPointerMove, onPointerUp]);

  const startDrag = useCallback(
    (index: number) => (e: React.PointerEvent<HTMLSpanElement>) => {
      if (isMobile) return;
      e.preventDefault();
      e.stopPropagation();
      e.currentTarget.setPointerCapture(e.pointerId);
      const cells = headerRef.current?.children;
      if (!cells) return;
      // Başlangıçtaki TÜM genişlikleri kaydet — sürükleme sırasında yalnız birisi değişsin.
      const initialWidths = Array.from(cells).map(
        (c) => (c as HTMLElement).getBoundingClientRect().width,
      );
      draggingRef.current = { index, initialWidths, startX: e.clientX };
      document.body.classList.add(DRAGGING_CLASS);
      document.addEventListener('pointermove', onPointerMove);
      document.addEventListener('pointerup', onPointerUp);
    },
    [isMobile, onPointerMove, onPointerUp],
  );

  const resizer = useCallback(
    (index: number): React.ReactNode => {
      if (isMobile) return null;
      return (
        <span
          role="separator"
          aria-orientation="vertical"
          className="col-resizer"
          title={t('columns.resizeHint')}
          onPointerDown={startDrag(index)}
          onDoubleClick={(e) => {
            e.stopPropagation();
            reset();
          }}
          onClick={(e) => e.stopPropagation()}
        />
      );
    },
    [isMobile, startDrag, reset],
  );

  return { style, headerRef, resizer, reset };
}
